// Assinatura confirmada (canvas B05). Espera o pagamento cair (até 30 s), mostra a fatura do Pix em aberto
// e, com o plano valendo, o que liberou agora.
import { useEffect, useRef, useState } from 'react';
import { DIAS_PARA_PAGAR_PIX, NOME_DO_PLANO, type Plano, type PlanoPago, liberouAgora, ordemDoPlano } from '@truco6/planos';
import { dataCompleta, dataDoCiclo } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { compra, medirUmaVez } from '../comum/medicao.ts';
import { ir } from '../comum/rotas.ts';
import { Icone, Moeda, useEu } from './planos-comum.tsx';
import { Marca } from '@truco6/ui/Marca';
import { Tela } from './comum.tsx';

const ESPERA_MS = 30_000;

export function AssinaturaOk({ sessao, plano, aoMudarPlano }: { sessao: Sessao; plano: PlanoPago | null; aoMudarPlano: (p: Plano) => void }) {
  const { eu, erro, carregar } = useEu(sessao);
  const [esgotou, setEsgotou] = useState(false);
  const avisou = useRef<Plano | null>(null);
  const alvo = plano ?? eu?.assinatura?.plano ?? null;
  const pronto = !!eu && !!alvo && ordemDoPlano(eu.plano) >= ordemDoPlano(alvo);
  const pix = eu?.assinatura?.status === 'aguardando_pix' ? eu.assinatura : null;

  // Consulta de 2 em 2 segundos até o plano valer. No Pix, segue consultando enquanto a pessoa paga.
  useEffect(() => {
    if (pronto || esgotou) return;
    const inicio = Date.now();
    const t = setInterval(() => {
      if (!pix && Date.now() - inicio > ESPERA_MS) { setEsgotou(true); clearInterval(t); return; }
      void carregar();
    }, 2000);
    return () => clearInterval(t);
  }, [pronto, esgotou, !!pix, carregar]);

  useEffect(() => {
    if (pronto && eu && avisou.current !== eu.plano) { avisou.current = eu.plano; aoMudarPlano(eu.plano); }
  }, [pronto, eu?.plano]);

  // Compra no GA4 quando o plano passa a valer. No Pix, só se a pessoa ainda estiver aqui quando o pagamento cair.
  const assinatura = eu?.assinatura;
  useEffect(() => {
    if (pronto && assinatura) {
      const id = `${assinatura.id}:${assinatura.fimDoPeriodo ?? 0}`;
      medirUmaVez('compra:' + id, 'purchase', compra(assinatura.plano, assinatura.ciclo, { transaction_id: id, payment_type: assinatura.metodo }));
    } else if (pix) {
      medirUmaVez('pix:' + pix.id + ':' + (pix.faturaAberta ?? ''), 'pix_gerado', compra(pix.plano, pix.ciclo));
    }
  }, [pronto, assinatura?.id, assinatura?.fimDoPeriodo, pix?.faturaAberta]);

  if (pronto && eu && alvo) {
    const vale = eu.plano as PlanoPago;
    const a = eu.assinatura;
    const novos = liberouAgora(vale);
    return (
      <Tela>
        <Marca altura={40} />
        <div className="duas-colunas">
          <div className={'heroi plano-' + vale}>
            <Moeda plano={vale} tamanho={136} />
            <h1>Você é {NOME_DO_PLANO[vale]}!</h1>
            <p className="sub">
              {a?.cortesia
                ? `${NOME_DO_PLANO[vale]} de cortesia${a.cortesiaAte ? ` até ${dataCompleta(a.cortesiaAte)}` : ''}.`
                : a?.fimDoPeriodo
                ? (a.cancelaNoFim ? `Vale até ${dataDoCiclo(a.fimDoPeriodo, a.ciclo)}.`
                  : a.ciclo === 'anual' ? `Plano anual ativo. Renova em ${dataDoCiclo(a.fimDoPeriodo, 'anual')}.`
                    : `Assinatura ativa. Próxima cobrança: ${dataDoCiclo(a.fimDoPeriodo, 'mensal')}.`)
                : 'Assinatura ativa.'}
            </p>
          </div>
          <div className="col">
            {novos.length > 0 && (
              <section aria-labelledby="h-liberou">
                <h2 id="h-liberou" className="rotulo">Liberou agora</h2>
                <ul className="cartao lista-liberou">
                  {novos.map((i) => <li key={i.recurso}><span>{i.texto}</span><Icone nome="check" /></li>)}
                </ul>
              </section>
            )}
            <div className="rodape-cta">
              <button className="btn1 largo" onClick={() => ir({ tela: 'nova-sala', ingresso: null }, true)}>CRIAR MINHA PRIMEIRA SALA</button>
              <button className="link" onClick={() => ir({ tela: 'meu-plano' }, true)}>Ver meu plano</button>
            </div>
          </div>
        </div>
      </Tela>
    );
  }

  if (pix && pix.faturaAberta) {
    return (
      <Tela>
        <Marca altura={40} />
        <div className={'heroi plano-' + pix.plano}>
          <Moeda plano={pix.plano} tamanho={96} />
          <h1>Falta pagar o Pix</h1>
          <p className="sub">O {NOME_DO_PLANO[pix.plano]} libera assim que o pagamento cair. A fatura vence em {DIAS_PARA_PAGAR_PIX} dias.</p>
        </div>
        <section className="cartao lista-info">
          <div><Icone nome="pix" /><p><b>Pague pelo app do banco</b><span>A página da fatura tem o QR Code e o Pix copia e cola.</span></p></div>
          <div><Icone nome="calendario" /><p><b>Também chega por e-mail</b>
            <span>{pix.ciclo === 'anual' ? 'Todo ano, a fatura nova vem com o Pix do ano.' : 'Todo mês, a fatura nova vem com o Pix do mês.'}</span></p></div>
        </section>
        <p className="carregando centro" role="status">Esperando o pagamento...</p>
        <div className="rodape-cta">
          <a className="btn1 largo botao-link" href={pix.faturaAberta} target="_blank" rel="noopener">PAGAR COM PIX</a>
          <button className="link" onClick={() => ir({ tela: 'meu-plano' }, true)}>Pagar depois</button>
        </div>
      </Tela>
    );
  }

  return (
    <Tela>
      <Marca altura={40} />
      <div className="heroi">
        {alvo && <Moeda plano={alvo} tamanho={96} />}
        <h1>{esgotou ? 'Ainda confirmando' : 'Confirmando o pagamento'}</h1>
        <p className="sub" role="status">
          {esgotou ? 'O pagamento ainda não caiu. Isso pode levar alguns minutos.' : 'Um instante, estamos falando com a Stripe...'}
        </p>
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {esgotou && (
        <div className="rodape-cta">
          <button className="btn2 largo" onClick={() => { setEsgotou(false); void carregar(); }}>Ver de novo</button>
          <button className="link" onClick={() => ir({ tela: 'meu-plano' }, true)}>Ver meu plano</button>
        </div>
      )}
    </Tela>
  );
}
